import { getPlanFieldConfig } from './planFieldConfigs';

/**
 * Validates a single field value against its configuration
 * @param {import('./planFieldConfigs').FieldConfig} field - Field configuration
 * @param {*} value - Current field value
 * @param {Object} allValues - All field values (for cross-field validation)
 * @returns {string|null} Error message or null if valid
 */
export const validateField = (field, value, allValues = {}) => {
  const { required, validation, type, label } = field;

  // Required check
  if (required && (value === undefined || value === null || value === '')) {
    return `${label} is required`;
  }

  // Skip further validation if empty and not required
  if (!value && value !== 0 && !required) return null;

  if (!validation) return null;

  const { min, max, minLength, maxLength, pattern, patternMessage } = validation;

  // Number validation
  if (type === 'number' && value !== '') {
    const numValue = Number(value);
    if (min !== undefined && numValue < min) {
      return `${label} must be at least ${min}`;
    }
    if (max !== undefined && numValue > max) {
      return `${label} must be at most ${max}`;
    }
  }

  // String length validation
  if (typeof value === 'string') {
    if (minLength !== undefined && value.length < minLength) {
      return `${label} must be at least ${minLength} characters`;
    }
    if (maxLength !== undefined && value.length > maxLength) {
      return `${label} must be at most ${maxLength} characters`;
    }
  }

  // Pattern validation
  if (pattern) {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    if (!regex.test(String(value))) {
      return patternMessage || `${label} format is invalid`;
    }
  }

  return null;
};

/**
 * Validates all visible fields in a configuration
 * @param {import('./planFieldConfigs').FieldConfig[]} fields - Array of field configs
 * @param {Object} values - Field values
 * @returns {Object.<string, string>} Map of field names to error messages
 */
export const validateFields = (fields, values) => {
  const errors = {};
  const visibleFields = fields.filter((f) => isFieldVisible(f, values));

  visibleFields.forEach((field) => {
    const error = validateField(field, values[field.name], values);
    if (error) {
      errors[field.name] = error;
    }
  });

  return errors;
};

/**
 * Checks if a field should be visible based on its condition
 * @param {import('./planFieldConfigs').FieldConfig} field - Field configuration
 * @param {Object} values - All field values
 * @returns {boolean} True if field should be visible
 */
export const isFieldVisible = (field, values) => {
  if (!field.condition) return true;

  // Function condition
  if (typeof field.condition === 'function') {
    return field.condition(values);
  }

  // Object condition
  const { field: condField, value: condValue, operator = 'equals' } = field.condition;
  const currentValue = values[condField];

  switch (operator) {
    case 'equals':
      return currentValue === condValue;
    case 'notEquals':
      return currentValue !== condValue;
    case 'in':
      return Array.isArray(condValue) && condValue.includes(currentValue);
    case 'notIn':
      return Array.isArray(condValue) && !condValue.includes(currentValue);
    case 'exists':
      return currentValue !== undefined && currentValue !== null && currentValue !== '';
    default:
      return true;
  }
};

/**
 * Extracts default values from field configurations
 * @param {import('./planFieldConfigs').FieldConfig[]} fields - Array of field configs
 * @returns {Object.<string, *>} Map of field names to default values
 */
export const getDefaultValues = (fields) => {
  const defaults = {};
  fields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      defaults[field.name] =
        typeof field.defaultValue === 'function' ? field.defaultValue() : field.defaultValue;
    }
  });
  return defaults;
};

/**
 * Gets default values for a specific plan
 * @param {string} planName - Name of the plan
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {Object.<string, *>} Map of field names to default values
 */
export const getPlanDefaultValues = (planName, orgContext = {}) => {
  const config = getPlanFieldConfig(planName, orgContext);
  if (!config?.fields) return {};
  return getDefaultValues(config.fields);
};

/**
 * Validates all custom fields for a specific plan
 * @param {string} planName - Name of the plan
 * @param {Object} values - Field values
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {Object.<string, string>} Map of field names to error messages
 */
export const validatePlanFields = (planName, values, orgContext = {}) => {
  const config = getPlanFieldConfig(planName, orgContext);
  if (!config?.fields) return {};
  return validateFields(config.fields, values);
};

/**
 * Checks if all required fields for a plan are filled
 * @param {string} planName - Name of the plan
 * @param {Object} values - Field values
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {boolean} True if all required visible fields have values
 */
export const areRequiredFieldsFilled = (planName, values, orgContext = {}) => {
  const errors = validatePlanFields(planName, values, orgContext);
  return Object.keys(errors).length === 0;
};
